export default {
    namespaced: true,

    state: {
        MIN_LINE_SIZE_LIMIT: 0.5,
        MIN_LINE_SIZE_LIMIT_FOR_SMALL_TRIM: 0.1,

        drawLineFromPoint: {
            x: 0,
            y: 0,
        },
        drawLineToPoint: {
            x: 0,
            y: 0,
        },

        ribExtraLength: (1/16), // starting length of the line with ribs has include an extra 1/16" for each of the ribs
        ribSideLegLength: 0.25,
        ribMainLegLength: 0.5,
    },

    getters: {
        getLineByPoints:(state, getters, rootState, rootGetters) => (p1, p2) => {
            let dx = p1.x - p2.x;
            let dy = p1.y - p2.y;

            let length = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
            let lengthInFeet =  Math.round(length / rootGetters['editor/footToPx'] * 100) / 100;

            let angle = Math.atan(dy / dx) * 180 / Math.PI || 0;
            let angle360 = rootGetters['angles/getAngle360'](dx, dy);

            let line = {
                fromPoint: p1,
                toPoint: p2,
                dx,
                dy,
                length,
                lengthInFeet,
                angle,
                angle360,
            };

            return line;
        },

        getLines:(state, getters) => (points) => {

            // line must have at least 2 points
            if (points.length < 2) return [];

            let lines = [];
            let prevPoint = points[0];

            for (let i = 1; i < points.length; i++) {
                let ribLinePoints = prevPoint.ribLinePoints || [];

                let type = ribLinePoints?.length ? 'lineWithRibs' : (prevPoint.type || 'simpleLine');

                let lineLengthIsHidden = prevPoint.lineLengthIsHidden || false;

                let line = {
                    id: prevPoint.id,
                    type,
                    ribLinePoints,
                    lineLengthIsHidden,
                    ...getters.getLineByPoints(prevPoint, points[i])
                };

                lines.push(line);

                prevPoint = points[i];
            }

            return lines;
        },

        getLinesWithoutFolds:(state, getters) => (points) => {
            let allLines = getters.getLines(points);

            let lines = [];

            allLines.forEach(line => {
                if (line.type !== 'fold') {
                    lines.push(line);
                }
            });

            return lines;
        },

        ribLegFoolLength(state) {
            return state.ribMainLegLength + state.ribSideLegLength * 2;
        },

        getLineWithRibsStartingLength:(state, getters, rootState, rootGetters) => (line) => {
            let ribsNumber = line.fromPoint.ribLinePoints.length / 2 - 1;

            // the line starting length include an extra 1/16" for each of the ribs
            let startingLength = +line.lengthInFeet + ( ribsNumber * state.ribExtraLength);

            return rootGetters['editor/fractionsDecimalRound'](startingLength);
        },

        getLineWithNewLength: (state, getters) => (fromPoint, toPoint, newLength) => {
            let originLine = getters.getLineByPoints(fromPoint, toPoint);

            // the line change direction depends how the start point (fromPoint) coordinates relate to the next one (toPoint)
            let direction = (toPoint.x - fromPoint.x) >= 0 ? 1 : -1;

            // convert from degrees to radians
            let angle = originLine.angle * Math.PI / 180;

            // standard trigonometry math
            let dx = direction * newLength * Math.cos(angle);
            let dy = direction * newLength * Math.sin(angle);

            // vertical line case
            if ((toPoint.x - fromPoint.x) === 0) {
                direction = (toPoint.y - fromPoint.y) >= 0 ? 1 : -1;

                dx = 0;
                dy = direction * newLength;
            }

            let newToPoint = {
                ...toPoint,
                x: fromPoint.x + dx,
                y: fromPoint.y + dy,
            };

            return {
                dx, dy,
                fromPoint,
                toPoint: newToPoint,
            };
        },

    },

    mutations: {
        setDrawLineFromPoint(state, point) {
            state.drawLineFromPoint = point;
        },

        setDrawLineToPoint(state, point) {
            state.drawLineToPoint = point;
        },

        setRibs(state, { point, ribLinePoints }) {
            point['ribLinePoints'] = ribLinePoints;
        },

        setLineType(state, { point, type }) {
            point['type'] = type;
        },

        setIsFoldStart(state, { point, isFoldStart }) {
            point['isFoldStart'] = isFoldStart;
        },

        setLineSizeFlip(state, { point, flip }) {
            point['sizeFlip'] = flip;
        },

        setLineIsHover(state, { point, isHover }) {
            point['isLineHover'] = isHover;
        },

    },

    actions: {
        drawLineUserAction({state, rootState, rootGetters, commit, dispatch}, { clickPosition, shape, drawingState }) {

            // on click we set start or end point of the line
            if (drawingState) {

                // drawing of the line was ended in this position
                let drawLineToPoint = clickPosition;

                let newLineToAdd = {
                    fromPoint: state.drawLineFromPoint,
                    toPoint: drawLineToPoint,
                };

                dispatch('addLine', newLineToAdd);

                // the drawing was ended
                commit('editor/setDrawingState', false, {root: true});

                // clear points data
                commit('setDrawLineFromPoint', {});
                commit('setDrawLineToPoint', {});
            }
            else {
                // get name of the shape
                const shapeName = shape.name();

                if (!shapeName) {
                    // drawing of the line has begun from empty space

                    // check if we already have a trim
                    if (rootGetters['editor/isTrimExists']) {
                        // need to do a warn for the user
                        console.warn('Trim exists - blocking empty space click. Points:', rootState['editor'].points);

                        // if so, just don't start draw the line
                        return;
                    }

                    // otherwise set start position of the line.
                    // both the start and the end of the line now are in the same position
                    commit('setDrawLineFromPoint', clickPosition);
                    commit('setDrawLineToPoint', clickPosition);

                }
                else if (shapeName === 'LineEndPointer') {

                    // drawing of the line has begun from one of the end pointers
                    const pointerPosition = shape.position();
                    const pointerId = shape.id();

                    // the starting edge of the line, placed at the pointer location
                    let drawLineFromPoint = {
                        id: pointerId,
                        ...pointerPosition
                    };

                    // the ending edge of the line follows the mouse pointer
                    let drawLineToPoint = {
                        id: pointerId + 1,
                        ...clickPosition
                    };

                    commit('setDrawLineFromPoint', drawLineFromPoint);
                    commit('setDrawLineToPoint', drawLineToPoint);
                }
                else {
                    return;
                }

                // the drawing was started
                commit('editor/setDrawingState', true, {root: true});
            }
        },

        checkLineLengthIsLittle({state, getters, rootState}, [p1, p2]) {
            return new Promise(resolve => {
                
                // don't check for the small trim mode
                if (rootState['trimInfo'].isSmallTrim) {
                    resolve(false);
                    return;
                }
                
                let line = getters.getLineByPoints(p1, p2);
                
                let isLineLengthTooSmall = line.lengthInFeet < state.MIN_LINE_SIZE_LIMIT;

                resolve(isLineLengthTooSmall);
            });
        },

        async addLine({state, rootState, dispatch}, line) {
            const points = rootState['editor'].points;

            const pointsNumber = points.length;
            const fromPointId = +line.fromPoint?.id;
            const newId = Date.now();

            let isLineLengthTooSmall = await dispatch('checkLineLengthIsLittle', [line.fromPoint, line.toPoint]);

            // check if the line is fold
            const isFoldLineType = line.fromPoint.isFoldStart || line.toPoint.isFoldStart;

            if (!isFoldLineType && isLineLengthTooSmall) {
                console.warn(`Line length is less than ${state.MIN_LINE_SIZE_LIMIT} feet`);

                return;
            }

            const pointDefaults = {
                isLineHover: false,
                isAngleHover: false,
                sizeFlip: false,
            };

            let endPoint = {
                id: newId,
                ...line.toPoint,
                ...pointDefaults,
            };

            // create first trim line
            if (!pointsNumber && !fromPointId) {
                let startPoint = {
                    id: newId - 1,
                    paintEnabled: true,
                    ...line.fromPoint,
                    ...pointDefaults,
                };

                let linePoints = [startPoint, endPoint];

                await dispatch('editor/setPoints', linePoints, {root: true});

                // after the first line was added to the trim, need to set the paint correct side
                await dispatch('paint/initPaintSide', null, {root: true});

                return endPoint;
            }


            // check if the line is adding to the start point of the trim
            const startPointId = +points[0].id;

            if(fromPointId === startPointId) {
                await dispatch('editor/addPointToStart', endPoint, {root: true});
            }
            else {
                // otherwise the line is adding to the end point of the trim
                await dispatch('editor/addPointToEnd', endPoint, {root: true});
            }

            return endPoint;
        },

        simpleLineLengthChange({getters, rootState, rootGetters, dispatch}, { line, lineLengthDiff, hideLineLength }) {
            let fromPoint = line.fromPoint;
            let toPoint = line.toPoint;

            // points position difference for the line new length
            let { dx, dy } = getters.getLineWithNewLength(fromPoint, toPoint, lineLengthDiff);

            // get the points index in the "store:state/points" array
            // to select the direction of the lines that will be shifted relative to the current line
            let fromPointIndex = rootGetters['editor/getPointIndexById'](fromPoint.id);
            let toPointIndex = rootGetters['editor/getPointIndexById'](toPoint.id);

            const points = rootState['editor'].points;
            let newPoints = [];

            // making a new array of points with shifting
            for (let i = 0; i < points.length; i++) {

                // select the direction of shifting
                if (fromPointIndex > toPointIndex) {

                    if (i <= fromPointIndex) {
                        // make shifting
                        let point = points[i];
                        point.x += dx;
                        point.y += dy;

                        newPoints[i] = point;
                    }
                    else {
                        // copy the point without shifting
                        newPoints[i] = points[i];
                    }

                }
                else {

                    if (i < toPointIndex) {
                        newPoints[i] = points[i];
                    }
                    else {
                        let point = points[i];
                        point.x += dx;
                        point.y += dy;

                        newPoints[i] = point;
                    }

                }

            }

            // set the lineLengthIsHidden to the fromPoint if need to hide the line length
            newPoints[fromPointIndex]['lineLengthIsHidden'] = hideLineLength;

            // to apply a new array of points
            dispatch('editor/setPoints', newPoints, {root: true});
        },

        makeLineWithRibs({getters, rootGetters, commit}, { line, ribsCount, lineLengthInFoots }) {
            const lineId = line.id;
            const footToPx = rootGetters['editor/footToPx'];

            const ribLengthInPx = getters.ribLegFoolLength * footToPx;
            const lineLengthInPx = lineLengthInFoots * footToPx;

            // the line contain ribs and the intervals between and around them
            let partsNumber = ribsCount + 1;

            // make all parts the same length
            let partLengthInPx = (lineLengthInPx - (ribLengthInPx * ribsCount)) / partsNumber;

            let ribLinePoints = [];

            for (let i = 0; i < (partsNumber + ribsCount + 1); i++) {
                let id = '' + lineId + i;

                // reshuffle position of each next point
                let xDistance = i%2 ? partLengthInPx : ribLengthInPx;

                // set x=0 for the first point
                let x = i ? ribLinePoints[i-1].x + xDistance : 0;

                let ribLinePoint = {
                    id,
                    x,
                    y: 0,
                };

                ribLinePoints.push(ribLinePoint);
            }

            commit('setRibs', { point: line.fromPoint, ribLinePoints })
        },

    }
}